import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtNumber, fmtDateShort } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { LoadMoreBar, useProgressiveList } from "../components/LoadMoreRow";
import { KpiTile } from "../components/intelligence/KpiTile";
import { CardMemoryTrigger } from "../components/card-memory/CardMemoryTrigger";
import { IconTruck, IconFuel } from "../components/icons";
import type { TransportFuelDashboardDto } from "@bi/types";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

/** km/l e R$/km com 2 casas (fmtNumber arredonda demais p/ esses indicadores). */
const fmt2 = (v: number | undefined) =>
  v === undefined ? "—" : v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function FuelPage() {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data, isLoading, isError: dataError, refetch: dataRefetch } = useQuery({
    queryKey: ["transport", "fuel", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportFuelDashboardDto>(
        `/dashboards/transport/fuel?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const s = data?.summary;
  const p = data?.prevSummary;
  const cmp = data?.comparisonLabel ?? "vs. mês anterior";

  // O card principal é o DIESEL, para poder ser conferido contra a Relação de
  // Abastecimentos; a comparação com o período anterior segue o mesmo recorte.
  const costDelta        = computeDelta(s?.dieselCost,      p?.dieselCost,      { direction: "higher_is_worse" });
  const litersDelta      = computeDelta(s?.dieselLiters,    p?.dieselLiters,    { direction: "neutral" });
  // minBase bem menor que o padrão (100): preço/litro, km/l e custo/km são
  // indicadores de escala pequena (ex. R$ 6,63, 2,43 km/l) — com o padrão, o
  // guard "base pequena demais pra significar algo" disparava sempre e o
  // delta nunca aparecia, mesmo havendo variação real período a período.
  const priceDelta       = computeDelta(s?.avgPricePerLiter, p?.avgPricePerLiter, { direction: "higher_is_worse", minBase: 1 });
  const consumptionDelta = computeDelta(s?.avgConsumption,  p?.avgConsumption,  { direction: "higher_is_better", minBase: 1 });
  const costPerKmDelta   = computeDelta(s?.costPerKm,       p?.costPerKm,       { direction: "higher_is_worse", minBase: 1 });
  const kmDelta          = computeDelta(s?.km,              p?.km,              { direction: "higher_is_better" });

  const byDay      = data?.byDay      ?? [];
  const costTrend  = data?.costTrend  ?? [];
  const byFuelType = data?.byFuelType ?? [];
  const topVehicles = data?.topVehicles ?? [];
  const vehicleList = useProgressiveList(topVehicles);
  // Barra de participação: relativa ao maior gasto da lista INTEIRA (não só
  // da página visível), pra escala não mudar ao clicar "carregar mais".
  const maxVehicleCost = Math.max(...topVehicles.map((v) => v.cost), 1);
  // Diesel + Arla + Gasolina somados é o gasto total do período — o % aqui é
  // real (participação no total), não uma escala relativa arbitrária.
  const totalFuelTypeCost = byFuelType.reduce((acc, f) => acc + f.cost, 0) || 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Combustível"
        subtitle="Gasto, consumo, custo por km e ranking de veículos por abastecimento"
      />

      {dataError && <QueryErrorBanner onRetry={() => { dataRefetch(); }} />}

      {/* KPIs — sem sparkline: `byDay`/`costTrend` somam TODOS os combustíveis
          (diesel+arla+gasolina), não só diesel, então não representam o
          número que estes cards mostram — melhor nenhuma série do que uma
          série que não bate com o valor do card. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <CardMemoryTrigger memory={{ cardKey: "combustivel.gasto-diesel", from, to, expected: s?.dieselCost, title: "Gasto com Diesel" }}>
          <KpiTile
            label="Gasto com Diesel"
            value={s ? fmtBRL(s.dieselCost) : "—"}
            delta={costDelta} deltaLabel={cmp}
            // Bate com o Total Geral da "Relação de Abastecimentos" do cliente.
            // O que sobra (arla + gasolina) está dito no footnote, não somado.
            footnote={s && s.otherFuelCost > 0
              ? `+ ${fmtBRL(s.otherFuelCost)} de arla e gasolina`
              : "confere com a Relação de Abastecimentos"}
            loading={isLoading}
          />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "combustivel.litros-diesel", from, to, expected: s?.dieselLiters, title: "Litros de Diesel" }}>
          <KpiTile
            label="Litros de Diesel"
            value={s ? fmtNumber(Math.round(s.dieselLiters)) : "—"}
            delta={litersDelta} deltaLabel={cmp}
            footnote={s && s.otherFuelLiters > 0
              ? `+ ${fmtNumber(Math.round(s.otherFuelLiters))} L de arla e gasolina`
              : undefined}
            loading={isLoading}
          />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "combustivel.preco-medio", from, to, expected: s?.avgPricePerLiter, title: "Preço Médio / Litro" }}>
          <KpiTile
            label="Preço Médio / Litro"
            value={s ? fmtBRL(s.avgPricePerLiter) : "—"}
            delta={priceDelta} deltaLabel={cmp}
            footnote="diesel"
            loading={isLoading}
          />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "combustivel.consumo", from, to, expected: s?.avgConsumption, title: "Consumo Médio" }}>
          <KpiTile
            label="Consumo Médio (km/l · diesel)"
            value={s ? fmt2(s.avgConsumption) : "—"}
            delta={consumptionDelta} deltaLabel={cmp}
            loading={isLoading}
          />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "combustivel.custo-km", from, to, expected: s?.costPerKm, title: "Custo por KM" }}>
          <KpiTile
            label="Custo por KM"
            value={s ? `R$ ${fmt2(s.costPerKm)}` : "—"}
            delta={costPerKmDelta} deltaLabel={cmp}
            loading={isLoading}
          />
        </CardMemoryTrigger>
        <CardMemoryTrigger memory={{ cardKey: "combustivel.km", from, to, expected: s?.km, title: "KM Rodados" }}>
          <KpiTile
            label="KM Rodados"
            value={s ? fmtNumber(Math.round(s.km)) : "—"}
            delta={kmDelta} deltaLabel={cmp}
            // Só o hodômetro do diesel (arla tem cadeia própria, que repete o mesmo
            // trecho) e só o intervalo fisicamente possível. Quando alguma linha é
            // descartada, o card diz — senão o número diverge do relatório sem
            // explicação (ver apps/api/src/shared/fuel-km.ts).
            footnote={s && s.discardedKmRefuels > 0
              ? `${s.discardedKmRefuels} abastecimento(s) com odômetro furado fora da conta`
              : "hodômetro dos abastecimentos de diesel"}
            loading={isLoading}
          />
        </CardMemoryTrigger>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Gasto com combustível por dia" subtitle="Valor no período" height={240}
          isEmpty={!isLoading && byDay.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={byDay} margin={{ top: 5, right: 5, left: -5, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-fuel" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--brand)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={fmtDateShort} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip
                formatter={(v: number, n: string) => [n === "liters" ? `${fmtNumber(v)} L` : fmtBRL(v), n === "liters" ? "Litros" : "Gasto"]}
                labelFormatter={fmtDateShort}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Area type="monotone" dataKey="cost" stroke="var(--brand)" strokeWidth={2} fill="url(#grad-fuel)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Evolução do gasto" subtitle="Por mês, nos últimos 5 meses" height={240}
          isEmpty={!isLoading && costTrend.length === 0} loading={isLoading}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={costTrend} margin={{ top: 5, right: 5, left: -5, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-fuel-trend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} />
              <Tooltip formatter={(v: number) => [fmtBRL(v), "Gasto"]} contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }} />
              <Bar dataKey="cost" fill="url(#grad-fuel-trend)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Ranking veículos + por tipo de combustível */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top vehicles */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconTruck className="w-4 h-4 text-gray-400" />
              Gasto por Veículo
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : topVehicles.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : vehicleList.visible.map((v, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{v.plate}</p>
                  <p className="text-xs text-gray-400">
                    {[v.brand, `${fmtNumber(Math.round(v.liters))} L`, v.costPerKm > 0 ? `R$ ${fmt2(v.costPerKm)}/km` : null].filter(Boolean).join(" · ")}
                  </p>
                  <div className="h-1 rounded-full bg-gray-100 overflow-hidden mt-1.5">
                    <div className="h-full rounded-full bg-tm-400" style={{ width: `${Math.min(100, Math.round((v.cost / maxVehicleCost) * 100))}%` }} />
                  </div>
                </div>
                <p className="text-sm font-semibold text-gray-900">{v.formatted}</p>
              </div>
            ))}
            {!isLoading && <LoadMoreBar list={vehicleList} label="veículos" />}
          </div>
        </div>

        {/* By fuel type */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <IconFuel className="w-4 h-4 text-gray-400" />
              Gasto por Tipo de Combustível
            </p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Carregando…</p>
            ) : byFuelType.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400 text-center">Sem dados no período.</p>
            ) : byFuelType.map((f, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{f.fuelType}</p>
                  <p className="text-xs text-gray-400">{fmtNumber(Math.round(f.liters))} L</p>
                  <div className="h-1 rounded-full bg-gray-100 overflow-hidden mt-1.5">
                    <div className="h-full rounded-full bg-tm-400" style={{ width: `${Math.round((f.cost / totalFuelTypeCost) * 100)}%` }} />
                  </div>
                </div>
                <p className="text-sm font-semibold text-tm-700 flex-shrink-0">
                  {f.formatted} <span className="text-xs text-gray-400 font-normal">({Math.round((f.cost / totalFuelTypeCost) * 100)}%)</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
